import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, model, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MultiSelect } from 'primeng/multiselect';
import { finalize } from 'rxjs';
import { ProductCategoryReportEntity } from '../product-category-report.model';
import { ProductCategoryReportService } from '../product-category-report.service';

@Component({
    selector: 'app-product-category-report-multi-select',
    imports: [FormsModule, MultiSelect],
    templateUrl: './product-category-report-multi-select.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductCategoryReportMultiSelectComponent {
    private destroyRef = inject(DestroyRef);
    private productCategoryReportService = inject(ProductCategoryReportService);

    selectedProductCategoryReportKeys = model<number[]>([]);

    id = input<string>('productCategoryReportMultiSelect');
    placeHolder = input<string | undefined>('Select products');

    subscriberKey = input.required<number | null>();

    invalid = input<boolean>(false);
    disabled = input<boolean>(false);

    options = signal<ProductCategoryReportEntity[]>([]);
    isLoading = signal<boolean>(false);
    loadingChange = output<boolean>();

    blur = output();
    
    isDisabled = computed(() => this.isLoading() || this.disabled());
    isInvalid = computed(() => this.invalid() && !this.isDisabled());

    constructor() {
        /* Fetch Options when Input Changes */
        effect(() => {
            const subscriberKey = this.subscriberKey();

            if (subscriberKey === null) {
                this.options.set([]);
            } else {
                this.loadOptions(subscriberKey);
            }
        });

        /* Loading Change */
		effect(() => {
			this.loadingChange.emit(this.isLoading());
		});
    }

    loadOptions(subscriberKey: number) {
        this.isLoading.set(true);
        this.options.set([]);

        this.productCategoryReportService
            .list(subscriberKey)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => {
                    this.isLoading.set(false);
                })
            )
            .subscribe((reportCategories) => {
                const sorted = reportCategories.sort((a, b) => a.productCategoryReportName.localeCompare(b.productCategoryReportName));
                this.options.set(sorted);
            });
    }

    onModelChange(productCategoryReportKeys: number[] | null) {
        this.selectedProductCategoryReportKeys.set(productCategoryReportKeys ?? []);
    }
}